import { useEffect, useRef, useState, type ReactNode } from "react";
import { CheckIcon, CopyIcon, ExternalIcon } from "@/components/icons";
import { inkButton, inkButtonLarge } from "@/components/quiet";
import { Button } from "@/components/ui/button";
import { ApiError, type api, type Site } from "../api.ts";
import {
  connectionArrived,
  INSTALLATION_CHECK_POLL_MS,
  INSTALLATION_CHECK_WINDOW_MS,
  setupStatus,
  type InstallationCheck,
  type SetupStatus,
} from "../installation-check.ts";
import { exactTime, relativeTime } from "../relative-time.ts";
import { sdkConfiguration, sdkInstallCommand } from "../sdk-setup.ts";
import { launchSiteReviewInNewTab } from "../site-launch.ts";

export interface InstallationCheckController {
  readonly check: InstallationCheck;
  /** Call inside the click: it opens the review tab synchronously. */
  readonly start: (site: Site) => void;
  readonly reset: () => void;
}

/**
 * Check installation: open the review in a new tab, then re-read the Site on
 * tab focus and every few seconds until `last_connected_at` proves a
 * connection, for up to two minutes.
 */
export function useInstallationCheck({
  csrf,
  onSite,
  request,
}: {
  readonly csrf: string;
  readonly onSite: (site: Site) => void;
  readonly request: typeof api;
}): InstallationCheckController {
  const [check, setCheck] = useState<InstallationCheck>({ kind: "idle" });
  const run = useRef(0);
  const onSiteRef = useRef(onSite);
  onSiteRef.current = onSite;

  function start(site: Site): void {
    run.current += 1;
    const current = run.current;
    const launch = launchSiteReviewInNewTab({
      open: () => window.open("", "_blank"),
      mint: () =>
        request<{ readonly launch_url: string }>(
          `/v1/appnotes/sites/${site.id}/token`,
          { method: "POST", csrf, body: { url: site.primary_url } },
        ),
    });
    if (launch.blocked) {
      setCheck({ kind: "blocked", siteId: site.id });
      return;
    }
    setCheck({
      kind: "waiting",
      siteId: site.id,
      baseline: site.last_connected_at ?? null,
      startedAt: Date.now(),
    });
    void launch.result.then((result) => {
      if (result.ok || run.current !== current) return;
      if (
        result.cause instanceof ApiError &&
        result.cause.code === "request_cancelled"
      )
        return;
      setCheck({
        kind: "failed",
        siteId: site.id,
        message:
          result.cause instanceof Error
            ? result.cause.message
            : "The request could not be completed.",
      });
    });
  }

  function reset(): void {
    run.current += 1;
    setCheck({ kind: "idle" });
  }

  useEffect(() => {
    if (check.kind !== "waiting") return;
    const waiting = check;
    const controller = new AbortController();
    let finished = false;
    let reading = false;

    async function read(): Promise<void> {
      if (finished || reading) return;
      reading = true;
      try {
        const site = await request<Site>(
          `/v1/appnotes/sites/${waiting.siteId}`,
          { signal: controller.signal },
        );
        if (finished) return;
        onSiteRef.current(site);
        if (
          connectionArrived({
            baseline: waiting.baseline,
            current: site.last_connected_at ?? null,
            startedAt: waiting.startedAt,
          })
        ) {
          finished = true;
          setCheck({ kind: "connected", siteId: waiting.siteId });
        }
      } catch {
        // A failed read is retried on the next tick; the window still ends.
      } finally {
        reading = false;
      }
    }

    const remaining = Math.max(
      0,
      waiting.startedAt + INSTALLATION_CHECK_WINDOW_MS - Date.now(),
    );
    const poll = window.setInterval(
      () => void read(),
      INSTALLATION_CHECK_POLL_MS,
    );
    const deadline = window.setTimeout(() => {
      void read().then(() => {
        if (finished) return;
        finished = true;
        setCheck({ kind: "missed", siteId: waiting.siteId });
      });
    }, remaining);
    const focused = () => void read();
    const visible = () => {
      if (document.visibilityState === "visible") void read();
    };
    window.addEventListener("focus", focused);
    document.addEventListener("visibilitychange", visible);
    return () => {
      finished = true;
      controller.abort();
      window.clearInterval(poll);
      window.clearTimeout(deadline);
      window.removeEventListener("focus", focused);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [check, request]);

  return { check, start, reset };
}

/**
 * Install view (render 10): the Site's name and host, the npm command, the
 * configuration, and one footer with the live status, the agent prompt and
 * Open site. Members can open it; nothing here is a secret.
 */
export function SiteInstallView({
  agentPrompt,
  apiOrigin,
  controller,
  site,
}: {
  readonly agentPrompt: string;
  readonly apiOrigin: string;
  readonly controller: InstallationCheckController;
  readonly site: Site;
}) {
  const status = setupStatus(site, controller.check);
  const configuration = sdkConfiguration(site.id, apiOrigin);
  return (
    <div className="site-flow" data-step="install">
      <SiteEyebrow site={site} />
      <h1>Install the SDK</h1>
      <p>Add the package, then mount it once in your app shell.</p>
      <CodeBlock copyLabel="Copy command" value={sdkInstallCommand}>
        {sdkInstallCommand}
      </CodeBlock>
      <CodeBlock copyLabel="Copy configuration" value={configuration}>
        <HighlightedConfiguration source={configuration} />
      </CodeBlock>
      <div className="site-flow-footer">
        <SetupStatusLine check={controller.check} site={site} status={status} />
        <QuietCopyButton label="Copy prompt for an agent" value={agentPrompt} />
        <Button
          className={inkButton}
          disabled={status === "archived"}
          onClick={() => controller.start(site)}
          type="button"
        >
          Open site
          <ExternalIcon />
          <span className="sr-only">(opens in a new tab)</span>
        </Button>
      </div>
      {status === "missed" ? (
        <MissedChecklist apiOrigin={apiOrigin} site={site} />
      ) : null}
    </div>
  );
}

/** Connected view (render 11): the first visit arrived. */
export function SiteConnectedView({
  launching,
  onBack,
  onOpen,
  site,
}: {
  readonly launching: boolean;
  readonly onBack: () => void;
  readonly onOpen: () => void;
  readonly site: Site;
}) {
  return (
    <div className="site-flow" data-step="connected">
      <SiteEyebrow site={site} />
      <h1>
        <span aria-hidden="true" className="site-flow-check">
          <CheckIcon />
        </span>
        Connected
      </h1>
      <p>
        AppNotes saw the first visit a moment ago. Open the site and press
        Comment to leave the first note.
      </p>
      <div className="site-flow-actions">
        <Button
          className={inkButtonLarge}
          disabled={launching}
          onClick={onOpen}
          type="button"
        >
          {launching ? "Opening…" : "Open site"}
          <ExternalIcon />
        </Button>
        <button className="text-action-lg" onClick={onBack} type="button">
          Back to Sites
        </button>
      </div>
    </div>
  );
}

function SiteEyebrow({ site }: { readonly site: Site }) {
  return (
    <p className="site-flow-eyebrow">
      {site.name}
      <span aria-hidden="true">·</span>
      {hostOf(site.origin)}
    </p>
  );
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

/** The footer's status: a dot and one line, read politely. */
function SetupStatusLine({
  check,
  site,
  status,
}: {
  readonly check: InstallationCheck;
  readonly site: Site;
  readonly status: SetupStatus;
}) {
  let text: ReactNode;
  switch (status) {
    case "never":
    case "waiting":
      text = "Waiting for the first visit";
      break;
    case "now":
      text = "Connected just now";
      break;
    case "earlier":
      text = (
        <span title={exactTime(site.last_connected_at ?? "")}>
          Connected {relativeLower(site.last_connected_at ?? "")}
        </span>
      );
      break;
    case "archived":
      text = "This site is archived. Restore it to check the installation.";
      break;
    case "blocked":
      text =
        "Your browser blocked the new tab. Allow pop-ups for AppNotes, or use Review site.";
      break;
    case "failed":
      text = (
        <>
          AppNotes could not open the site.{" "}
          {check.kind === "failed" ? check.message : null}
        </>
      );
      break;
    case "missed":
      text = <>No visit from {hostOf(site.origin)} yet.</>;
      break;
  }
  return (
    <p
      aria-live="polite"
      className="site-flow-status"
      data-status={status}
      role="status"
    >
      <span aria-hidden="true" className="site-flow-dot" />
      <span>{text}</span>
    </p>
  );
}

function relativeLower(value: string): string {
  const label = relativeTime(value);
  return /^[A-Z][a-z]/u.test(label)
    ? label.charAt(0).toLowerCase() + label.slice(1)
    : label;
}

function MissedChecklist({
  apiOrigin,
  site,
}: {
  readonly apiOrigin: string;
  readonly site: Site;
}) {
  return (
    <ul className="setup-checklist">
      <li>
        The page at <Mono>{site.primary_url}</Mono> loads{" "}
        <Mono>@plannotator/appnotes</Mono> and calls <Mono>mount()</Mono>.
      </li>
      <li>
        <Mono>siteId</Mono> is <Mono>{site.id}</Mono> and <Mono>apiOrigin</Mono>{" "}
        is <Mono>{apiOrigin}</Mono>.
      </li>
      <li>
        The page is served from exactly <Mono>{site.origin}</Mono>. Another
        subdomain, port, or http is a different site.
      </li>
      <li>
        If the page sets a Content Security Policy, <Mono>connect-src</Mono>{" "}
        allows <Mono>{apiOrigin}</Mono>.
      </li>
    </ul>
  );
}

function Mono({ children }: { readonly children: ReactNode }) {
  return (
    <span className="font-mono text-[12px] text-foreground">{children}</span>
  );
}

function HighlightedConfiguration({ source }: { readonly source: string }) {
  const parts: ReactNode[] = [];
  const pattern = /("(?:[^"\\]|\\.)*")|\b(import|from|const|await)\b/gu;
  let last = 0;
  for (const match of source.matchAll(pattern)) {
    const index = match.index;
    if (index > last) parts.push(source.slice(last, index));
    parts.push(
      <span
        className={match[1] === undefined ? "code-keyword" : "code-string"}
        key={index}
      >
        {match[0]}
      </span>,
    );
    last = index + match[0].length;
  }
  if (last < source.length) parts.push(source.slice(last));
  return <>{parts}</>;
}

/** Soft code block with a quiet copy icon in its top right corner. */
export function CodeBlock({
  children,
  copyLabel,
  value,
}: {
  readonly children: ReactNode;
  readonly copyLabel: string;
  readonly value: string;
}) {
  const [state, setState] = useCopyState();
  return (
    <div className="code-block">
      <pre>
        <code>{children}</code>
      </pre>
      <button
        aria-label={state === "copied" ? "Copied" : copyLabel}
        className="code-block-copy"
        onClick={() => void copyText(value, setState)}
        type="button"
      >
        {state === "copied" ? <CheckIcon /> : <CopyIcon />}
      </button>
      <span className="sr-only" role="status">
        {state === "copied"
          ? "Copied"
          : state === "failed"
            ? "Copy failed. Select the text and copy it."
            : ""}
      </span>
    </div>
  );
}

/** A quiet text action that copies a value and says so for 1.5 s. */
export function QuietCopyButton({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  const [state, setState] = useCopyState();
  return (
    <button
      className="text-action-strong"
      onClick={() => void copyText(value, setState)}
      type="button"
    >
      <span aria-live="polite">
        {state === "copied"
          ? "Copied"
          : state === "failed"
            ? "Copy failed"
            : label}
      </span>
    </button>
  );
}

type CopyState = "idle" | "copied" | "failed";

function useCopyState(): [CopyState, (state: CopyState) => void] {
  const [state, setState] = useState<CopyState>("idle");
  useEffect(() => {
    if (state === "idle") return;
    const timeout = window.setTimeout(() => setState("idle"), 1_500);
    return () => window.clearTimeout(timeout);
  }, [state]);
  return [state, setState];
}

async function copyText(
  value: string,
  setState: (state: CopyState) => void,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    setState("copied");
  } catch {
    setState("failed");
  }
}
